import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { MarketPage, RoutePage, SportType } from '@/interface/enum';
import { useFeatureToggleStore } from '@/stores/featureToggleStore';
import { useRootStore } from '@/stores/rootStore';

export default function useZoneBanner() {
    const { marketPage, sportType } = storeToRefs(useRootStore());
    const {
        isSportsCupZoneEnabled,
        isMultipleSportsZoneEnabled,
        multipleSportsTournamentNames,
        sportsCupTournamentNames,
        supportedSportsForFlagBanner: sportsCupZoneSupportSports,
        multipleSportsZoneSupportSports,
    } = storeToRefs(useFeatureToggleStore());

    const isZoneBannerEnabled = computed<boolean>(() => {
        const isTwoToggleOnAtSameTime = isSportsCupZoneEnabled.value && isMultipleSportsZoneEnabled.value;
        if (isTwoToggleOnAtSameTime) {
            console.warn('Two toggle on at same time is NOT allowed');
            return false;
        }
        return isSportsCupZoneEnabled.value || isMultipleSportsZoneEnabled.value;
    });

    const route = useRoute();
    const isShowFlagsBanner = computed<boolean>(() => {
        if (marketPage.value !== MarketPage.Live) {
            return false;
        }
        switch (route.name) {
            case RoutePage.SportsCupPage:
            case RoutePage.MultipleSportsZone:
                return false;
            default:
                break;
        }
        return isZoneBannerEnabled.value;
    });

    const zoneTitleKey = computed(() => {
        if (!isShowFlagsBanner.value) {
            return null;
        }
        return isSportsCupZoneEnabled.value
            ? 'sportsCupZone_title'
            : 'multipleSports_title';
    });

    const zoneRoute = computed(() => {
        if (!isShowFlagsBanner.value) {
            return null;
        }

        if (isSportsCupZoneEnabled.value) {
            return {
                name: RoutePage.SportsCupPage,
            };
        }

        return {
            name: RoutePage.MultipleSportsZone,
            params: {
                sportRouteName: multipleSportsZoneSupportSports.value.some(sport => sport === sportType.value)
                    ? SportType.toRouteName(sportType.value)
                    : SportType.toRouteName(SportType.Soccer),
            },
        };
    });

    const leagueNames = computed(() => {
        if (isSportsCupZoneEnabled.value) {
            return sportsCupTournamentNames.value;
        }

        if (isMultipleSportsZoneEnabled.value) {
            return multipleSportsTournamentNames.value;
        }

        return undefined;
    });

    const supportedSportList = computed(() => (isMultipleSportsZoneEnabled.value ? multipleSportsZoneSupportSports.value : sportsCupZoneSupportSports.value));

    const sportTypeForQuery = computed(() => (supportedSportList.value.includes(sportType.value) ? sportType.value : sportsCupZoneSupportSports.value[0]));

    return {
        isShowFlagsBanner,
        zoneTitleKey,
        zoneRoute,
        isZoneBannerEnabled,
        leagueNames,
        sportTypeForQuery,
    };
}
